import JsSIP from 'jssip';
import type { UA } from 'jssip';
import { Emitter } from './events';
import { Logger } from './logger';
import { MediaController } from './media';
import { Call } from './call';
import { decodeToken, isExpired } from './token';
import { resolveIceServers } from './ice';
import { secureContextReason, reasonOf, describeFailure } from './util';
import type { PhoneOptions, AuthCredentials, CallOptions, TokenClaims, MediaDevices, FailureReason } from './types';

// Platform defaults. The WSS host is the FQDN (its TLS cert is issued for the
// name, not the bare IP); the SIP realm is the FreeSWITCH realm. These rarely
// change and are overridable via PhoneOptions for testing against another box.
const DEFAULT_WSS = 'wss://rtc.sautipbx.com:7443';
const DEFAULT_REALM = '102.221.34.71';

// Default cap on ICE gathering (ms) — see PhoneOptions.iceGatheringTimeout.
const DEFAULT_ICE_GATHERING_TIMEOUT = 3000;

// Platform limit for customPayload (spec §11.1). Enforced client-side so the
// developer gets an immediate, clear error rather than a rejected call.
const MAX_CUSTOM_PAYLOAD = 500;
const CUSTOM_PAYLOAD_HEADER = 'X-Sauti-Custom-Payload';

/** Events a {@link Phone} emits. */
export interface PhoneEvents {
  /** Registered with the switch — ready to place/receive calls. */
  registered: { extension: string; account: string };
  /** Unregistered (after {@link Phone.unregister} or the socket dropping). */
  unregistered: void;
  registrationFailed: FailureReason;
  /** WebSocket connected. */
  connected: void;
  /** WebSocket dropped (cert untrusted? IP not allow-listed? box down?). */
  disconnected: { reason?: string };
  /** An inbound call is arriving — answer or reject the given {@link Call}. */
  incoming: Call;
}

/**
 * A browser softphone. Authenticate with an ephemeral token minted by your
 * backend, then place and receive real calls.
 *
 * @example
 * ```js
 * const phone = new VoiceSDK.Phone({ logLevel: 'info' });
 * await phone.authenticate(tokenFromYourBackend);
 * const call = await phone.call('+254711111111');
 * phone.on('incoming', (c) => c.answer());
 * ```
 */
export class Phone extends Emitter<PhoneEvents> {
  private ua: UA | null = null;
  private log: Logger;
  private media: MediaController;
  private claims: TokenClaims | null = null;
  private iceServers: RTCIceServer[] = [];

  constructor(private options: PhoneOptions = {}) {
    super();
    this.log = new Logger(options.logLevel ?? 'none', options.onLog);
    this.media = new MediaController(this.log);
  }

  /** The extension this phone is registered as, or `null` before auth. */
  get extension(): string | null {
    return this.claims?.sub ?? null;
  }

  /** The account uuid this phone belongs to, or `null` before auth. */
  get account(): string | null {
    return this.claims?.acc ?? null;
  }

  get isRegistered(): boolean {
    return this.ua?.isRegistered() ?? false;
  }

  /**
   * Register the phone using an ephemeral token (or `{ token, iceServers }` from
   * your backend's mint call). Resolves once REGISTERED, rejects if registration
   * fails. The token is decoded locally only to build the SIP URI — never
   * verified here (the switch does that).
   */
  authenticate(auth: string | AuthCredentials): Promise<void> {
    const token = typeof auth === 'string' ? auth : auth.token;
    const authIce = typeof auth === 'string' ? undefined : auth.iceServers;

    const claims = decodeToken(token);
    if (!claims || !claims.sub) {
      return Promise.reject(new Error('Invalid phone token — expected a JWT carrying a `sub` (extension) claim.'));
    }
    if (isExpired(claims)) {
      return Promise.reject(new Error('Phone token has already expired — mint a fresh one from your backend.'));
    }
    this.claims = claims;
    this.iceServers = resolveIceServers(authIce, this.options.iceServers);

    // Re-authenticating: tear down any prior registration first.
    if (this.ua) {
      try {
        this.ua.stop();
      } catch {
        /* ignore */
      }
      this.ua = null;
    }

    const wss = this.options.wssUrl ?? DEFAULT_WSS;
    const realm = this.options.realm ?? DEFAULT_REALM;
    const socket = new JsSIP.WebSocketInterface(wss);

    // The token is presented as BOTH the SIP auth username and the password, and
    // as the SIP URI user (the AoR). FreeSWITCH's dynamic directory validates the
    // token and echoes it back as the digest password, so the real SIP secret
    // never leaves the server; it 403s a REGISTER whose AoR differs from the
    // authenticated id, which is why the URI user must be the token too. A
    // `number-alias` on the server keeps the registration dialable as the human
    // extension.
    const config = {
      sockets: [socket],
      uri: `sip:${token}@${realm}`,
      authorization_user: token,
      password: token,
      register: true,
      session_timers: false,
      pcConfig: this.peerConfig(),
    };

    if (this.options.logLevel === 'debug') JsSIP.debug.enable('JsSIP:*');
    else JsSIP.debug.disable();

    const ua = new JsSIP.UA(config);
    this.ua = ua;
    this.bindUA(ua);

    return new Promise<void>((resolve, reject) => {
      const onRegistered = (): void => {
        cleanup();
        resolve();
      };
      const onFailed = (r: FailureReason): void => {
        cleanup();
        reject(new Error('Registration failed: ' + describeFailure(r)));
      };
      const cleanup = (): void => {
        this.off('registered', onRegistered);
        this.off('registrationFailed', onFailed);
      };
      this.on('registered', onRegistered);
      this.on('registrationFailed', onFailed);
      ua.start();
    });
  }

  /**
   * Place an outbound call. `destination` may be a bare extension/number
   * (`'2002'`, `'+254711111111'`) or a full SIP URI. Returns the {@link Call} to
   * wire events and controls onto.
   */
  call(destination: string, options: CallOptions = {}): Call {
    if (!this.ua) throw new Error('Not authenticated — call authenticate() first.');

    const blocked = secureContextReason();
    if (blocked) throw new Error(blocked);

    const realm = this.options.realm ?? DEFAULT_REALM;
    const target = destination.includes('@')
      ? destination.startsWith('sip:')
        ? destination
        : 'sip:' + destination
      : `sip:${destination}@${realm}`;

    const session = this.ua.call(target, {
      mediaConstraints: this.media.audioConstraints(),
      pcConfig: this.peerConfig(),
      extraHeaders: this.customPayloadHeaders(options.customPayload),
    });
    this.log.info('calling', target);
    return new Call(session, this.media, this.log, this.peerConfig(), this.iceGatheringTimeout());
  }

  /** Unregister and close the connection. */
  unregister(): void {
    this.ua?.stop();
  }

  /** List available audio input/output devices. */
  listDevices(): Promise<MediaDevices> {
    return this.media.listDevices();
  }

  /** Choose the microphone used for the next call. */
  setInputDevice(deviceId: string): void {
    this.media.setInputDevice(deviceId);
  }

  /** Route call audio to a specific speaker (where the browser supports it). */
  setOutputDevice(deviceId: string): Promise<void> {
    return this.media.setOutputDevice(deviceId);
  }

  /** Set output volume, 0–1. */
  setVolume(value: number): void {
    this.media.setVolume(value);
  }

  private peerConfig(): RTCConfiguration {
    return { iceServers: this.iceServers };
  }

  private iceGatheringTimeout(): number {
    return this.options.iceGatheringTimeout ?? DEFAULT_ICE_GATHERING_TIMEOUT;
  }

  private customPayloadHeaders(payload?: string): string[] | undefined {
    if (!payload) return undefined;
    if (payload.length > MAX_CUSTOM_PAYLOAD) {
      throw new Error(`customPayload exceeds the ${MAX_CUSTOM_PAYLOAD}-character limit.`);
    }
    // A SIP header is a single line; embedded CR/LF would be header injection.
    if (/[\r\n]/.test(payload)) {
      throw new Error('customPayload must not contain newline characters.');
    }
    return [`${CUSTOM_PAYLOAD_HEADER}: ${payload}`];
  }

  private bindUA(ua: UA): void {
    ua.on('connected', () => this.emit('connected'));
    ua.on('disconnected', (e) => this.emit('disconnected', { reason: (e as { reason?: string })?.reason }));
    ua.on('registered', () => {
      this.log.info('registered as', this.claims?.sub);
      this.emit('registered', { extension: this.claims!.sub, account: this.claims!.acc });
    });
    ua.on('unregistered', () => this.emit('unregistered'));
    ua.on('registrationFailed', (e) => {
      const r = reasonOf(e);
      this.log.error('registration failed', describeFailure(r));
      this.emit('registrationFailed', r);
    });
    ua.on('newRTCSession', (e) => {
      const session = (e as { session: import('jssip').RTCSession }).session;
      if (session.direction !== 'incoming') return;
      const call = new Call(session, this.media, this.log, this.peerConfig(), this.iceGatheringTimeout());
      this.log.info('incoming call from', call.remoteIdentity);
      this.emit('incoming', call);
    });
  }
}
